import styled from '@emotion/styled';
import { ClickAwayListener, useTheme } from '@mui/material';
import { PopupContextProvider } from '@kc-console/common-ui/singleton';
import { BannerProvider, ROUTE_PROGRESS_BAR_ID, useLayoutVisible } from '@kc-console/context';

import { ResponsiveMetric } from '../../../constants';
import { useMegaMenuState } from '../../../hooks';
import { Gnb, GnbTopBar, Lnb, MegaMenu } from '../../AppMenu';
import { ConsoleVersionBanner } from '../../ConsoleVersionBanner';
import RouteProgressBar from './RouteProgressBar';

import type { CommonLayoutProps } from '../types';

const LayoutContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-width: ${ResponsiveMetric.EXTRA_SMALL_MAX}px;
`;

const PageLayout = styled('div')`
  display: flex;
  flex: 1;
  overflow-y: auto;
`;

const MainContent = styled('main')`
  display: flex;
  flex: 1;
  overflow-x: auto;
`;

const GnbContainer = styled.div`
  max-width: 100vw;
  min-width: ${ResponsiveMetric.EXTRA_SMALL_MAX}px;
  overflow: hidden;
  z-index: ${({ theme }) => theme.zIndex.navigationOverlay};
`;

const GeneralLayout = ({ children, pageProps }: CommonLayoutProps) => {
  const theme = useTheme();
  const { setIsOpen } = useMegaMenuState();
  const { gnb, lnb } = useLayoutVisible();

  return (
    <LayoutContainer id={ROUTE_PROGRESS_BAR_ID}>
      <RouteProgressBar />
      {gnb && (
        // GNB 내부 dropdown/popover는 body portal로 렌더되므로 GNB(navigationOverlay=1300)보다 위인 navigationTop(1310) 계층을 공유한다.
        <PopupContextProvider zIndex={theme.zIndex.navigationTop}>
          <GnbContainer>
            <Gnb />
            <GnbTopBar />
          </GnbContainer>
        </PopupContextProvider>
      )}
      <ClickAwayListener onClickAway={() => setIsOpen(false)}>
        {/* MegaMenu 내부 hover popper도 body portal로 렌더되므로 MegaMenu(navigationOverlay=1300)보다 위인 navigationTop(1310) 계층을 공유한다. */}
        <PopupContextProvider zIndex={theme.zIndex.navigationTop}>
          <div>
            <MegaMenu />
          </div>
        </PopupContextProvider>
      </ClickAwayListener>
      <PageLayout>
        <Lnb hide={!lnb} />
        {/* 본문 영역 popup/dropdown은 form footer(stickyActionBar=1290) 위, GNB(navigationOverlay=1300) 아래인 contentPopup(1295) 계층을 사용한다. */}
        <PopupContextProvider zIndex={theme.zIndex.contentPopup}>
          <BannerProvider value={{ Banner: ConsoleVersionBanner }}>
            <MainContent>{children}</MainContent>
          </BannerProvider>
        </PopupContextProvider>
      </PageLayout>
    </LayoutContainer>
  );
};

export default GeneralLayout;
